/**
 * useOrderHistory Hook
 * Manages fetching and displaying order history.
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this hook only fetches order data.
 * All order operations are handled by backend Order Service.
 */

import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchOrders, cancelOrder } from '../../../store/slices/ordersSlice';
import {
  selectAllOrders,
  selectPendingOrders,
  selectExecutedOrders,
  selectFailedOrders,
  selectOrdersLoading,
  selectOrdersError,
} from '../../../store/selectors/orderSelectors';

export interface UseOrderHistoryOptions {
  /**
   * Filter by order status
   */
  status?: string;
  /**
   * Filter by broker account ID
   */
  brokerAccountId?: string;
  /**
   * Filter by instrument ID
   */
  instrumentId?: string;
  /**
   * Limit number of orders
   */
  limit?: number;
  /**
   * Offset for pagination
   */
  offset?: number;
  /**
   * Auto-refresh interval in milliseconds (0 to disable)
   */
  autoRefreshInterval?: number;
}

export interface UseOrderHistoryResult {
  /**
   * All orders (filtered if options provided)
   */
  orders: ReturnType<typeof selectAllOrders>;
  /**
   * Pending orders
   */
  pendingOrders: ReturnType<typeof selectPendingOrders>;
  /**
   * Executed orders
   */
  executedOrders: ReturnType<typeof selectExecutedOrders>;
  /**
   * Failed orders
   */
  failedOrders: ReturnType<typeof selectFailedOrders>;
  /**
   * Loading state
   */
  loading: boolean;
  /**
   * Error message
   */
  error: string | null;
  /**
   * Refresh orders list
   */
  refresh: () => Promise<void>;
  /**
   * Cancel an order
   */
  cancel: (platformOrderId: string) => Promise<void>;
}

export function useOrderHistory(options: UseOrderHistoryOptions = {}): UseOrderHistoryResult {
  const dispatch = useAppDispatch();
  const {
    status,
    brokerAccountId,
    instrumentId,
    limit = 50,
    offset = 0,
    autoRefreshInterval = 0,
  } = options;

  // Selectors
  const allOrders = useAppSelector(selectAllOrders);
  const loading = useAppSelector(selectOrdersLoading);
  const error = useAppSelector(selectOrdersError);

  // Filtered orders
  const filteredOrders = useAppSelector((_state) => {
    let orders = allOrders;

    if (status) {
      // Case-insensitive status matching
      const statusLower = status.toLowerCase();
      orders = orders.filter((order) => order.status?.toLowerCase() === statusLower);
    }
    if (brokerAccountId) {
      orders = orders.filter((order) => order.broker_account_id === brokerAccountId);
    }
    if (instrumentId) {
      orders = orders.filter((order) => order.instrument_id === instrumentId);
    }

    return orders;
  });

  const pendingOrders = useAppSelector(selectPendingOrders);
  const executedOrders = useAppSelector(selectExecutedOrders);
  const failedOrders = useAppSelector(selectFailedOrders);

  /**
   * Refresh orders list
   */
  const refresh = useCallback(async () => {
    try {
      await dispatch(
        fetchOrders({
          status,
          instrument_id: instrumentId,
          limit,
          offset,
        })
      ).unwrap();
    } catch (err) {
      // Error handled by Redux slice
      // eslint-disable-next-line no-console
      console.error('Failed to refresh orders:', err);
    }
  }, [dispatch, status, instrumentId, limit, offset]);

  /**
   * Cancel an order
   */
  const cancel = useCallback(
    async (platformOrderId: string) => {
      try {
        await dispatch(cancelOrder(platformOrderId)).unwrap();
        // Refresh orders after cancellation
        await refresh();
      } catch (err) {
        // Error handled by Redux slice
        // eslint-disable-next-line no-console
        console.error('Failed to cancel order:', err);
        throw err;
      }
    },
    [dispatch, refresh]
  );

  /**
   * Initial load
   */
  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Auto-refresh interval
   */
  useEffect(() => {
    if (autoRefreshInterval > 0) {
      const interval = setInterval(() => {
        refresh();
      }, autoRefreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefreshInterval, refresh]);

  return {
    orders: filteredOrders,
    pendingOrders,
    executedOrders,
    failedOrders,
    loading,
    error,
    refresh,
    cancel,
  };
}

